import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';

// Backend runs on http://localhost:5270 (from launchSettings.json)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5270/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getClientContextHeader = (): string | null => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  try {
    const uaData = (navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string; version: string }> } }).userAgentData;
    const browserFromBrands = uaData?.brands?.map((b) => b.brand).join(', ') || null;
    const deviceType = /mobile|iphone|android/i.test(navigator.userAgent)
      ? 'Mobile'
      : /ipad|tablet/i.test(navigator.userAgent)
        ? 'Tablet'
        : 'Desktop';

    const payload = {
      browser: browserFromBrands,
      platform: navigator.platform || null,
      device: deviceType,
      language: navigator.language || null,
      userAgent: navigator.userAgent || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    };

    return btoa(JSON.stringify(payload));
  } catch {
    return null;
  }
};

// Add interceptors for user/session headers
api.interceptors.request.use((config) => {
  const userId = localStorage.getItem('userId');
  const sessionId = localStorage.getItem('sessionId');
  
  if (userId) {
    config.headers['X-User-Id'] = userId;
  }
  if (sessionId) {
    config.headers['X-Session-Id'] = sessionId;
  }
  
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) {
    config.headers['Authorization'] = `Bearer ${adminToken}`;
  }
  
  const adminId = localStorage.getItem('adminId');
  if (adminId) {
    config.headers['X-Admin-Id'] = adminId;
  }

  config.headers['X-Device-Id'] = getDeviceId();
  const clientContext = getClientContextHeader();
  if (clientContext) {
    config.headers['X-Client-Context'] = clientContext;
  }
  
  return config;
});

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if it's a 401 Unauthorized error
    if (error.response && error.response.status === 401) {
      // Check if the request was to an admin endpoint
      const isAdminRequest = error.config?.url?.includes('/admin');
      
      if (isAdminRequest) {
        // Clear admin authentication data
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminId');
        
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/admin/login') {
          // Redirect to admin login page
          window.location.href = '/admin/login';
        }
      }
    }
    
    // Re-throw the error so it can be handled by the calling code
    return Promise.reject(error);
  }
);

export default api;

